import { useClientAttachmentSettingsBootstrap } from '@ms/yammer-hooks/dist/clientAttachmentSettings';
import { useIsTeamsQnaInOneEventsLandingPage } from '@ms/yammer-hooks/dist/teamsMeetings';
import { useCurrentLocale } from '@ms/yammer-i18n-react';
import { AppContextProvider } from '@ms/yammer-libs-app-context';
import { MediaLightboxStoreProvider } from '@ms/yammer-medialightbox';
import { ReduxAnnouncerBridge } from '@ms/yammer-web-components/dist/accessibility/Announcer';
import type { ThreadIconOptions } from '@ms/yammer-web-components/dist/feed/ThreadIconOptions';
import { ThreadIconOptionsProvider, defaultOptions } from '@ms/yammer-web-components/dist/feed/ThreadIconOptions';
import type { LinkRenderingOptions } from '@ms/yammer-web-components/dist/link/YammerLink';
import { LinkRenderingOptionsProvider } from '@ms/yammer-web-components/dist/link/YammerLink';
import MediaLightbox from '@ms/yammer-web-components/dist/messageAttachments/MediaLightbox';
import { LoadableRealtimeClient } from '@ms/yammer-web-components/dist/realtime/RealtimeClient';
import SessionContextProviderWithHooks from '@ms/yammer-web-components/dist/session/SessionContextProviderWithHooks';
import AppContainer from '@ms/yammer-web-core-ui/dist/components/AppContainer';
import { BrandAccentColorContext } from '@ms/yammer-web-core-ui/dist/util/brandContext';
import { usePrepareIcons } from '@ms/yammer-web-core-ui/dist/util/icons';
import { ContainerWidthProvider } from '@ms/yammer-web-core-ui/dist/util/responsiveness';
import { getCoreUITokenOverridesForTeams } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import { useFluent9TeamsTheme, useTeamsMeetingTheme } from 'hooks';
import { useShouldEmulateHighContrastInTeams } from 'hooks/useShouldEmulateHighContrastInTeams';
import { useThemeWithTeamsOverridesForV8, useThemeWithTeamsOverridesForV9 } from 'hooks/useThemeWithTeamsOverrides';
import type { FC } from 'react';
import React from 'react';
import { useIsCached } from 'state/teams';

import AppLayout from '../AppLayout';
import Routes from '../Routes';
import TeamsClientProvider from '../TeamsClientProvider';

import { getClassNames } from './App.styles.legacy';

const teamsClientContextOptions = {
  shouldHideLeftSidebar: true,
  shouldHideHeaders: true,
};
const teamsMeetingClientAttachmentSettingsOverrides = { attachmentsDisabled: true };
const linkRenderingOptions: Partial<LinkRenderingOptions> = {
  disableLivePersonaCard: true,
  shouldOpenAskMeAnythingLinkInNewWindow: true,
  shouldOpenGroupLinkInNewWindow: true,
  shouldOpenThreadLinkInNewWindow: true,
  shouldOpenTagLinkInNewWindow: true,
  shouldOpenTopicLinkInNewWindow: true,
  shouldOpenUserLinkInNewWindow: true,
  disableUserLinks: true,
  shouldOpenEventLinkInNewWindow: true,
  shouldOpenViewConversationLinkInNewWindow: true,
  shouldOpenYammerLinkInNewWindow: true,
  isThreadStarterCopyButtonLinkExternal: true,
  shouldShowBackButton: false,
};
const teamsMeetingIconOptions: Partial<ThreadIconOptions> = {
  commandBarBasicIcons: {
    ...defaultOptions.commandBarBasicIcons,
    reply: 'Reply',
  },
};
const brandColorOptions = {
  linksUseBrandColor: true,
};

const App: FC = () => {
  useClientAttachmentSettingsBootstrap(teamsMeetingClientAttachmentSettingsOverrides);
  const teamsMeetingTheme = useTeamsMeetingTheme();
  const fluent9TeamsTheme = useFluent9TeamsTheme();
  const shouldEmulateHighContrastInTeams = useShouldEmulateHighContrastInTeams();

  const teamsMeetingFluent8Theme = useThemeWithTeamsOverridesForV8(teamsMeetingTheme);
  const teamsMeetingFluent9Theme = useThemeWithTeamsOverridesForV9(fluent9TeamsTheme);
  const locale = useCurrentLocale();
  const isCached = useIsCached();
  const isTeamsQnaInEventPage = useIsTeamsQnaInOneEventsLandingPage();

  usePrepareIcons();

  const classNames = getClassNames();

  return (
    <div className={classNames.app}>
      <AppContainer
        theme={teamsMeetingFluent8Theme}
        fluent9Theme={teamsMeetingFluent9Theme}
        emulateHighContrast={shouldEmulateHighContrastInTeams}
        locale={locale}
        coreuiTokenOverrides={getCoreUITokenOverridesForTeams(teamsMeetingFluent8Theme.isInverted)}
        useTransparentBackground={isTeamsQnaInEventPage}
      >
        <AppContextProvider mainColumnAriaRole="region">
          <SessionContextProviderWithHooks>
            <TeamsClientProvider options={teamsClientContextOptions}>
              <LinkRenderingOptionsProvider options={linkRenderingOptions}>
                <ThreadIconOptionsProvider options={teamsMeetingIconOptions}>
                  <ContainerWidthProvider>
                    <BrandAccentColorContext.Provider value={brandColorOptions}>
                      <MediaLightboxStoreProvider>
                        <AppLayout>
                          <Routes />
                          <LoadableRealtimeClient isRealtimeDisabled={isCached} />
                        </AppLayout>
                        <MediaLightbox />
                      </MediaLightboxStoreProvider>
                    </BrandAccentColorContext.Provider>
                  </ContainerWidthProvider>
                </ThreadIconOptionsProvider>
              </LinkRenderingOptionsProvider>
            </TeamsClientProvider>
          </SessionContextProviderWithHooks>
          <ReduxAnnouncerBridge />
        </AppContextProvider>
      </AppContainer>
    </div>
  );
};

export default App;
